import { useState } from 'react';
import { AdminCompanyChat } from '../../../api/types';
import { LoadingState } from '../../../components/LoadingState';
import { ChatMessageBubble } from './ChatMessageBubble';
import './CompanyChats.css';

export function CompanyChatsTab({ chats, companyId, errorMessage, isLoading }: { chats: AdminCompanyChat[]; companyId: number; errorMessage: string | null; isLoading: boolean }) {
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const selectedChat = chats.find((chat) => chat.chat_id === selectedChatId) ?? chats[0] ?? null;

  return (
    <section className="admin-detail-card company-chats">
      <div className="admin-detail-card__header">
        <div>
          <p className="admin-eyebrow">Коммуникации</p>
          <h2>Чаты компании</h2>
          <p>Диалоги компании с покупателями и поставщиками доступны администратору только для просмотра.</p>
        </div>
        <span className="admin-page-header__count">{chats.length}</span>
      </div>

      <>{isLoading ? <LoadingState ariaLabel="Загрузка чатов" /> : null}{errorMessage ? <p role="alert" className="admin-error">{errorMessage}</p> : null}</>

      {!isLoading && chats.length > 0 ? (
        <div className={`communications-workspace admin-communications${selectedChatId !== null ? ' has-selection' : ''}`}>
          <nav aria-label="Диалоги компании" className="communications-inbox">
            {chats.map((chat) => {
              const lastMessage = chat.messages[chat.messages.length - 1] ?? null;
              const isSelected = selectedChat?.chat_id === chat.chat_id;

              return (
                <button aria-pressed={isSelected} className="communications-dialog" key={chat.chat_id} onClick={() => setSelectedChatId(chat.chat_id)} type="button">
                  <span aria-hidden="true" className="communications-dialog__avatar">{chat.title.slice(0, 1).toUpperCase()}</span><span className="communications-dialog__copy"><span className="company-chat-list__topline">
                    <strong>{chat.title}</strong>
                    <time>{formatChatDate(chat.updated_at)}</time>
                  </span>
                  <span className="company-chat-list__meta">{getChatEntityLabel(chat.entity_code)} · {chat.participant_count} участника</span>
                  <span className="company-chat-list__preview">{lastMessage?.text ?? 'В диалоге пока нет сообщений'}</span></span>
                </button>
              );
            })}
          </nav>

          {selectedChat !== null ? (
            <article className="communications-conversation chat-panel" aria-label={`Сообщения: ${selectedChat.title}`}>
              <div className="communications-mobile-navigation"><button className="workspace-secondary-button" type="button" onClick={() => setSelectedChatId(null)}>← Все чаты</button></div><header className="chat-panel__header">
                <div>
                  <strong>{selectedChat.title}</strong>
                  <span>{getChatEntityLabel(selectedChat.entity_code)} · запись #{selectedChat.record_id}</span>
                </div>
                <span className={selectedChat.is_closed ? 'admin-status-chip admin-status-chip_neutral' : 'admin-status-chip admin-status-chip_success'}>
                  {selectedChat.is_closed ? 'Закрыт' : 'Активен'}
                </span>
              </header>
              <div className="chat-feed">
                {selectedChat.messages.map((message) => {
                  const isCompanyMessage = message.author_company_id === companyId;

                  return (
                    <ChatMessageBubble key={message.message_id} author={message.author_name} text={message.text} createdAt={message.created_at} isOwn={isCompanyMessage} />
                  );
                })}
                {selectedChat.messages.length === 0 ? <p className="admin-empty">В этом диалоге пока нет сообщений.</p> : null}
              </div>
              <footer>Режим просмотра · ответы отправляются участниками из личного кабинета</footer>
            </article>
          ) : null}
        </div>
      ) : null}

      {!isLoading && errorMessage === null && chats.length === 0 ? (
        <div className="admin-empty-state">
          <strong>Диалогов пока нет</strong>
          <p>Они появятся здесь, когда компания начнёт переписку по заказу или с контрагентом.</p>
        </div>
      ) : null}
    </section>
  );
}

function getChatEntityLabel(entityCode: string): string {
  if (entityCode === 'order') {
    return 'Заказ';
  }

  if (entityCode === 'company') {
    return 'Диалог компаний';
  }

  return 'Диалог';
}

function formatChatDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' }).format(date);
}
